import { UploadAssetFieldMui } from '@app/modules/assets/screens/upload/components/UploadAssetFieldMui';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { SelectElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { FrameImageToFieldHandler } from './FrameImageToFieldHandler';
import { FrameSelectorFieldMui } from './FrameSelectorFieldMui';

interface ThumbFieldsMuiProps {
  /**
   * The field name for the thumbnail URL
   * @default 'thumbnailUrl'
   */
  urlFieldName?: string;
  /**
   * Whether to show the FrameImageToField component
   * @default false
   */
  showFrameImageToField?: boolean;
  /**
   * Maximum size in MB for image upload
   * @default 2
   */
  maxSizeMb?: number;
  /**
   * Helper text for the image upload field
   */
  helperText?: string;
  /**
   * Alternative video URL to be passed to FrameSelectorFieldMui
   */
  videoUrl?: string;
}

export function ThumbFieldsMui({
  urlFieldName = 'thumbnailUrl',
  showFrameImageToField = false,
  maxSizeMb = 2,
  helperText,
  videoUrl,
}: ThumbFieldsMuiProps) {
  const { setValue } = useFormContext();
  const thumbType = useWatch({ name: 'thumbType' });
  const { $t } = useIntl();

  useEffect(() => {
    // reset on thumbType change
    if (thumbType) {
      setValue(urlFieldName, null);
      setValue('thumbOffsetMs', null);
    }
  }, [thumbType, urlFieldName, setValue]);

  const thumbTypeId = typeof thumbType === 'object' ? thumbType?.id : thumbType;

  return (
    <Box>
      <Box sx={{ width: 300 }}>
        <SelectElement
          name="thumbType"
          label={$t({
            id: 'common.thumbType',
            defaultMessage: 'Tipo de thumbnail',
          })}
          options={[
            {
              id: 'DEFAULT',
              label: 'Default',
            },
            {
              id: 'IMAGE_URL',
              label: $t({
                id: 'common.customImage',
                defaultMessage: 'Imagen custom',
              }),
            },
            {
              id: 'VIDEO_FRAME',
              label: $t({
                id: 'common.videoFrame',
                defaultMessage: 'Frame de vídeo',
              }),
            },
          ]}
          fullWidth
        />
      </Box>

      {thumbTypeId === 'IMAGE_URL' && (
        <Box mb={2}>
          <UploadAssetFieldMui
            name={urlFieldName}
            label={$t({
              id: 'common.thumbnail',
              defaultMessage: 'Miniatura',
            })}
            inputProps={{
              label: $t({
                id: 'common.loadImage',
                defaultMessage: 'Cargar imagen',
              }),
              size: 'sm',
              accept: 'image/*',
              maxSizeMb,
              helperText,
            }}
          />
        </Box>
      )}

      {thumbTypeId === 'VIDEO_FRAME' && (
        <Box sx={{ width: 300 }}>
          <FrameSelectorFieldMui unit={'ms'} name={'thumbOffsetMs'} videoUrl={videoUrl}>
            {showFrameImageToField && (
              <FrameImageToFieldHandler timeField={'thumbOffsetMs'} urlField={urlFieldName} />
            )}
          </FrameSelectorFieldMui>
        </Box>
      )}
    </Box>
  );
}
